import { format as formatDateFns, isSameDay } from 'date-fns'
import { ar, enGB } from 'date-fns/locale'
import { isolateFormattedValue } from './bidi'
import { intlLocale, type Locale } from './locales'

/**
 * Locale-aware formatting for numbers, currency and dates.
 *
 * ## The Arabic numeral decision
 *
 * Arabic has two digit forms: Western Arabic (`0123456789`) and Eastern
 * Arabic-Indic (`٠١٢٣٤٥٦٧٨٩`). `Intl` defaults `ar` to the Eastern form.
 *
 * **Multaqa ERP uses Western digits in every locale.** Reasons, in order of weight:
 *
 * 1. Numbers in an ERP are identifiers as often as they are quantities —
 *    invoice numbers, tax registration numbers, SKUs, bank details. Those are
 *    matched against externally-issued documents that use Western digits.
 * 2. Data leaves the system: CSV exports, PDF invoices, accounting integrations.
 *    Mixed digit forms across exports are a reconciliation problem.
 * 3. Western digits are the prevailing convention in business and financial
 *    software across the Gulf and Levant, which is the target market.
 *
 * This is a product decision, not a technical limitation, and it is reversible:
 * drop the `-u-nu-latn` extension in `intlLocale()` and everything follows.
 *
 * ## Money
 *
 * `formatMoney` takes a **decimal string** and an ISO-4217 currency code, never
 * a number. `Intl.NumberFormat` formats decimal strings exactly, so no IEEE-754
 * double ever appears in the path between the database and the screen.
 *
 * See docs/architecture/0012-money-representation.md. The frontend formats
 * amounts; it never computes them.
 */

const numberFormatters = new Map<string, Intl.NumberFormat>()

function numberFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(options)}`
  let formatter = numberFormatters.get(key)

  if (!formatter) {
    formatter = new Intl.NumberFormat(intlLocale(locale), options)
    numberFormatters.set(key, formatter)
  }

  return formatter
}

export function formatNumber(
  value: number,
  locale: Locale,
  options: Intl.NumberFormatOptions = {},
): string {
  return isolateFormattedValue(numberFormatter(locale, options).format(value), locale)
}

export function formatPercent(value: number, locale: Locale, fractionDigits = 1): string {
  return isolateFormattedValue(
    numberFormatter(locale, {
      style: 'percent',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value),
    locale,
  )
}

const DECIMAL_STRING = /^-?\d+(?:\.\d+)?$/

/**
 * Narrows a validated decimal string to the type `Intl.NumberFormat` accepts.
 *
 * ECMA-402 NumberFormat V3 lets `format()` take a decimal string and render it
 * exactly, which is what keeps an amount off the IEEE-754 path entirely. The
 * type it wants is the template literal `` `${number}` ``, so a runtime check
 * and a type predicate are what connect a plain `string` to it.
 */
function isDecimalString(value: string): value is Intl.StringNumericLiteral {
  return DECIMAL_STRING.test(value)
}

/**
 * @param amount   Decimal string at the column's full scale, e.g. `'1234.5600'`.
 *                 A `number` is rejected: by the time one exists the precision
 *                 may already be gone, and no check here can detect that.
 * @param currency ISO-4217 code that travels with the amount, e.g. `'EGP'`.
 */
export function formatMoney(
  amount: string,
  currency: string,
  locale: Locale,
  options: Intl.NumberFormatOptions = {},
): string {
  if (typeof amount !== 'string' || !isDecimalString(amount)) {
    throw new TypeError(
      `formatMoney expects a decimal string such as '1234.5600', received ${JSON.stringify(amount)}. Monetary amounts must never travel as JavaScript numbers.`,
    )
  }

  // An empty currency can occur while an empty tenant is loading. Intl rejects
  // it as an invalid currency code, so render the amount without a symbol until
  // the company currency is available. The string is still handed to Intl
  // unparsed; converting to Number would reintroduce the precision loss this
  // formatter is designed to avoid.
  if (currency.trim() === '') {
    return isolateFormattedValue(
      numberFormatter(locale, { style: 'decimal', ...options }).format(amount),
      locale,
    )
  }

  return isolateFormattedValue(
    numberFormatter(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      ...options,
    }).format(amount),
    locale,
  )
}

/**
 * A quantity held at the column's full scale, rendered the way a human writes it.
 *
 * Quantities are `NUMERIC(19,6)` (NFR-DAT-014), so they arrive as `'3.000000'`
 * and printing the string verbatim puts `3.000000` on an invoice line. The
 * trailing zeros carry no information: only the digits the value actually uses
 * are kept, and grouping separators come from the locale.
 *
 * Resilient against:
 * - JavaScript numbers (e.g. integer counts `0`, `5`, or computed numbers `3.5`)
 * - Missing / nullish values (`null`, `undefined`, `''` -> formats as `'0'`)
 * - Grouping commas (e.g. `'1,250.50'`)
 * - Leading dot decimals (e.g. `'.5'`)
 * - Non-numeric strings (gracefully returns the string rather than throwing)
 *
 * @param value Decimal string, number, or nullish quantity value.
 */
export function formatQuantity(value: string | number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined) {
    return isolateFormattedValue(numberFormatter(locale, { style: 'decimal' }).format(0), locale)
  }

  let str: string
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return isolateFormattedValue('—', locale)
    }
    str = Math.abs(value) < 1e-6 && value !== 0 ? value.toFixed(6) : String(value)
  } else if (typeof value === 'string') {
    str = value.trim()
    if (str === '') {
      return isolateFormattedValue(numberFormatter(locale, { style: 'decimal' }).format(0), locale)
    }
  } else {
    return isolateFormattedValue(String(value), locale)
  }

  // Strip grouping commas if standard thousands formatting
  const withoutCommas = str.replace(/,/g, '')
  if (isDecimalString(withoutCommas)) {
    str = withoutCommas
  }

  // Normalize leading dot like ".5" or "-.5"
  if (str.startsWith('.')) {
    str = `0${str}`
  } else if (str.startsWith('-.')) {
    str = `-0${str.slice(2)}`
  }

  // Normalize negative zero or zero padding like "-0", "-0.0000", "0.000"
  if (/^-?0+(\.0+)?$/.test(str)) {
    str = '0'
  }

  if (!isDecimalString(str)) {
    return isolateFormattedValue(str, locale)
  }

  const [, fraction = ''] = str.split('.')
  const significant = fraction.replace(/0+$/, '').length

  return isolateFormattedValue(
    numberFormatter(locale, {
      style: 'decimal',
      minimumFractionDigits: significant,
      maximumFractionDigits: significant,
    }).format(str),
    locale,
  )
}

const DATE_FNS_LOCALES = { en: enGB, ar } as const

export function formatDate(value: Date, locale: Locale, pattern = 'PP'): string {
  return isolateFormattedValue(
    formatDateFns(value, pattern, { locale: DATE_FNS_LOCALES[locale] }),
    locale,
  )
}

export function formatDateRange(from: Date, to: Date, locale: Locale, pattern = 'PP'): string {
  const formattedFrom = formatDate(from, locale, pattern)
  if (isSameDay(from, to)) return formattedFrom
  return `${formattedFrom} – ${formatDate(to, locale, pattern)}`
}

/**
 * Dates displayed in a fixed, unambiguous form regardless of locale.
 *
 * Use for anything that identifies a record — a posting date on a journal
 * entry, a document date on an invoice — where "05/03" being read as 3 May or
 * 5 March is a real cost.
 */
export function formatIsoDate(value: Date): string {
  return formatDateFns(value, 'yyyy-MM-dd')
}

/**
 * A duration in minutes, read as hours and minutes: `7h 30m` in English,
 * `7 س 30 د` in Arabic.
 *
 * The unit words come from `Intl` rather than from a translation file. An
 * abbreviation for "hour" is a locale rule, not a product string, and `Intl`
 * already carries it for every locale the package supports.
 *
 * A zero part is omitted: `45m`, not `0h 45m`, and `7h`, not `7h 0m`. Design
 * §10.1 sketches a payslip in a monospace block where both parts always
 * appear, but that is column alignment in an ASCII drawing, not a rule about
 * the formatter. §10.4 specifies `7h 30m` and nothing else. A whole zero
 * duration reads `0m`, because a duration with no parts at all reads as
 * missing data rather than as none.
 *
 * Not to be confused with `formatClockDuration` in the company app's
 * `lib/clock-duration.ts`, which returns clock-style `07:30`. Two names, two
 * meanings.
 */
export function formatMinutesAsHours(minutes: number, locale: Locale): string {
  if (!Number.isFinite(minutes)) {
    return isolateFormattedValue('—', locale)
  }

  const total = Math.round(Math.abs(minutes))
  const hours = Math.floor(total / 60)
  const rest = total % 60

  const parts: string[] = []
  if (hours > 0) {
    parts.push(durationFormatter(locale, 'hour').format(hours))
  }
  if (rest > 0 || hours === 0) {
    parts.push(durationFormatter(locale, 'minute').format(rest))
  }

  const sign = minutes < 0 && total > 0 ? '-' : ''

  return isolateFormattedValue(`${sign}${parts.join(' ')}`, locale)
}

function durationFormatter(locale: Locale, unit: 'hour' | 'minute'): Intl.NumberFormat {
  return numberFormatter(locale, { style: 'unit', unit, unitDisplay: 'narrow' })
}
